<template>
	<div id="app">
		<CanaryMarker />

		<AppHeader v-if="$route.name !== 'app.streaming-charts'" />

		<NuxtPage />

		<StyleInjector />

		<ModalsContainer />

		<!-- Load initTrackers -->
		<!-- Load components: ConsentBanner, MobileBanner -->
		<LowPriorityComponents />
	</div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import { Platform } from '@/@types/graphql-types';
import AppHeader from '@/components/nav/AppHeader.vue';

import StyleInjector from '@/components/StyleInjector';

import {
	RegisterSessionStartDocument,
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables,
} from '@/graphql/mutation/RegisterSessionStart.mutation';

import { enableScroll } from '@/helpers/scroll-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';
import { useExperimentInitialization } from '@/libraries/experiment';

import CanaryMarker from '@/ui-components/CanaryMarker.vue';

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { ModalsContainer } from 'vue-final-modal';
import { jw_changeLocale } from '@/routing';

const LowPriorityComponents = defineAsyncComponent(() => import('@/components/LowPriorityComponents.vue'));

if (import.meta.client) {
	import('@/helpers/tracking/cwv-helper').then(({ initCoreWebVitalsMonitor }) =>
		initCoreWebVitalsMonitor({ nuxt_enabled: true })
	);
}

const metaStore = useMetaStore();
const routingStore = useRoutingStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();

useHead(() => metaStore.head);

const { initializeExperiments } = useExperimentInitialization();
const { initLoginFlow } = useLoginFlow();

const {
	mutate: registerSessionStart,
	onError,
	onDone,
} = useMutation<RegisterSessionStartMutation, RegisterSessionStartMutationVariables>(RegisterSessionStartDocument);

onError(error => {
	captureMessageForSentry('SSR App: Unable to fire Register Session Start Mutation', {
		where: '[SSRApp.vue] onMounted()',
		error,
	});
});

onDone(data => {
	const experiments = data?.registerSessionStart.experiments ?? [];
	initializeExperiments(
		experiments.map(el => ({
			countries: el.countries,
			languages: el.languages,
			name: el.name,
			sample: el.sample,
			variant: el.variant ?? null,
		}))
	);
});

watch(
	() => languageStore.webLocale,
	async (webLocale, oldWebLocale) => {
		if (webLocale === oldWebLocale) return;
		jw_changeLocale(webLocale, routingStore.activeDomain);
		await languageStore.initLanguage();
	},
	{ immediate: false }
);

const runtimeConfig = useRuntimeConfig().public;
console.log(`Nuxt Setup: ${runtimeConfig.buildConfig.commitShortHash}`);

onMounted(() => {
	void initLoginFlow();

	nextTick(() => {
		const int = setInterval(() => {
			// remove loader
			const loader = document.querySelector('#loader');
			loader?.parentNode?.removeChild(loader);
			clearInterval(int);
		}, 50);

		// reactivate scroll on page rendered
		enableScroll();
	});

	// this causes interaction lag on safari browsers
	// userStore.setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
	registerSessionStart({
		input: {
			platform: Platform.Web,
			language: languageStore.language,
			country: languageStore.country,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		},
	});

	TrackingHelper.isInitializedPromise.then(() => {
		//need to initialize PASubgenres for it to populate trackers as SEO user
		TrackingHelper.refreshPermanentAudiencesSubgenres([]);
		TrackingHelper.refreshPermanentAudiences(userStore.permanentAudiences);

		// route.fullPath is for Home as first page, which has incomplete urlMetadata
		if ((routingStore.urlMetadata?.fullPath ?? route.fullPath) == null) return;

		TrackingHelper.trackAudienceRefresh(userStore.permanentAudiences);
	});
});
</script>

<style lang="scss">
@import '@/styles/variables.scss';

body {
	overflow-x: hidden;
}

#app {
	position: relative;
	z-index: 0;
	background: var(--jw-background-color) !important;
}
</style>
